import { HomeGreeting } from '../ui/HomeGreeting';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { pulseHealth, pulseIncidentList, pulseIncidentsV3, pulseProjections } from '@aithora/shared';
import { useConsoleRole } from '../session/SessionContext';
import { useNavBadges, usePageTab } from '../shell/Shell';
import { useReadOnlyReason } from '../ui/ReadOnly';
import { sx } from '../ui/designStyles';
import { Station } from './pulse/Station';
import { IncidentsTab, effState, incRef, type Inc, type IncFilter } from './pulse/SetupIncidents';
import { OnCallTab } from './pulse/SetupOnCall';
import { ConnectorsTab, ModelsTab, ProjectedTab, SecurityTab, fmtMins, soonestOf } from './pulse/SetupTabs';
import { SPEC, durNoSec, env, pre, usePulseApi } from './pulse/lib';
import './pulse/pulse.css';
import { useCustomerText } from '../ui/buildLabels';

type SetupTab = 'Incidents' | 'On-Call' | 'Connectors' | 'Models' | 'Security' | 'Projected';
const TABS: [string, SetupTab | null][] = [
  ['Live', null],
  ['Incidents', 'Incidents'],
  ['On-call', 'On-Call'],
  ['Connectors & probes', 'Connectors'],
  ['Models', 'Models'],
  ['Security', 'Security'],
];
const tabStyle = (on: boolean) =>
  'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#22272E;color:#fff;font-weight:700;' : 'background:#FCFAF4;color:#3C454F;font-weight:600;border:1px solid rgba(34,39,46,.16);');

/**
 * System Pulse (design isPulse, T7358-8537). Super Admin, Operator and Dev get the full
 * station plus the setup tabs (Dev reads resident initials only and cannot write). A
 * Facility Admin gets the same Live layout for their own building, read-only, with no
 * tabs; the server scopes every route to their building.
 */
export function Pulse() {
  const plain = useCustomerText();
  const { role } = useConsoleRole();
  const roReason = useReadOnlyReason();
  const FA = role === 'ADMIN';
  const dev = role === 'DEVELOPER';
  // Writes: Aithora's own writers only (the server refuses Dev, Partner and a Facility Admin too).
  const canWrite = (role === 'SUPER_ADMIN' || role === 'OPERATOR') && roReason === null;
  const writeTitle = roReason ?? (canWrite ? undefined : 'Read only for your role: the server refuses changes from it too.');
  // ?tab=Incidents opens a setup tab directly (Oversight's "See all" link).
  const { search } = useLocation();
  const [tab, setTab] = useState<SetupTab | null>(() => {
    const t = new URLSearchParams(search).get('tab');
    return TABS.some(([, k]) => k !== null && k === t) ? (t as SetupTab) : null;
  });
  const [incFilter, setIncFilter] = useState<IncFilter>('Open');
  const [incOpen, setIncOpen] = useState<string | null>(null);
  const [ackedHere, setAckedHere] = useState<Set<string>>(new Set());
  usePageTab(FA ? null : TABS.find((t) => t[1] === tab)?.[0] ?? 'Live');

  const setup = !FA && tab !== null;
  const badges = useNavBadges();
  const health = usePulseApi((c, signal) => env(pulseHealth(c, { signal })), [], setup);
  const incs = usePulseApi((c, signal) => env(pulseIncidentList(c, { query: { windowDays: 7 }, signal })), [], setup);
  const v3 = usePulseApi((c, signal) => pulseIncidentsV3(c, { signal }), [], setup && (tab === 'Incidents' || tab === 'Projected'));
  const proj = usePulseApi((c, signal) => pulseProjections(c, { signal }), [], setup);

  const openIncident = (id: string) => {
    const i = incs.data?.incidents.find((x) => x.id === id);
    setIncFilter(i ? (effState(i, ackedHere) === 'open' ? 'Open' : effState(i, ackedHere) === 'acked' ? 'Acked' : 'Resolved') : 'All');
    setIncOpen(id);
    setTab('Incidents');
  };
  const now = Date.now();
  const incidents: Inc[] = incs.data?.incidents ?? [];

  // ── verdict strip (design puVerdictLine / puBeatAge / puForeOneLine) ──
  const checks = (health.data?.groups ?? []).flatMap((g) => g.checks);
  const downN = checks.filter((c) => c.status === 'down').length;
  const degN = checks.filter((c) => c.status === 'degraded').length;
  // The headline is System Pulse's one sentence (the menu label's, which Oversight prints
  // too): open incidents and building safety links, each named. The service probes stay on
  // their own line in Live.
  const pulseB = badges.data?.badges.pulse ?? null;
  const roll = pulseB ? (pulseB.tone === 'red' ? 'down' : pulseB.tone === 'amber' ? 'degraded' : 'ok') : downN ? 'down' : degN ? 'degraded' : 'ok';
  const lead = incidents.find((i) => i.status === 'open') ?? incidents.find((i) => i.status === 'acknowledged') ?? null;
  const leadAcked = lead ? lead.status === 'acknowledged' || ackedHere.has(lead.id) : false;
  const leadTail = lead ? ' · ' + incRef(lead) + ' open ' + durNoSec((now - Date.parse(lead.openedAt)) / 60000) + ', ' + (leadAcked ? (ackedHere.has(lead.id) ? 'acked by you' : 'acked') : 'unacked') : '';
  const verdict = pulseB
    ? pulseB.tone === 'green'
      ? 'Every system and building OK'
      : (pulseB.aria || pulseB.label || '') + leadTail
    : health.error
    ? 'Service health could not be read'
    : !health.data
      ? '–'
      : downN
        ? downN + (downN === 1 ? ' service down' : ' services down') + (degN ? ', ' + degN + ' degraded' : '') + (lead ? ' · ' + incRef(lead) + ' open ' + durNoSec((now - Date.parse(lead.openedAt)) / 60000) + ', ' + (leadAcked ? (ackedHere.has(lead.id) ? 'acked by you' : 'acked') : 'unacked') : '')
        : degN
          ? degN + (degN === 1 ? ' service degraded' : ' services degraded') + ' · surfaced, not paging'
          : 'All probed signals healthy';
  const beat = health.data ? 'last beat ' + Math.max(0, Math.round((now - Date.parse(health.data.checkedAt)) / 1000)) + 's ago' : '';
  const soonest = soonestOf(proj.data?.projections ?? []);
  const foreLine = proj.error ? plain('Projections are not served yet') : soonest === null ? 'No SLO is projected to breach' : 'Next SLO breach projected in ' + fmtMins(soonest);

  const onAcked = (id: string, on: boolean) => {
    setAckedHere((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
    incs.reload();
  };

  return (
    <div data-screen-label="Pulse" style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')} data-testid={FA ? 'pulse-admin' : 'pulse'}>
      {/* System Pulse is a Developer's home (their first page): it opens on their greeting (Andrew, 2026-10-19). */}
      {dev && <HomeGreeting />}
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0;')} role={FA ? undefined : 'tablist'} aria-label={FA ? undefined : 'System Pulse'}>
        {!FA &&
          TABS.map(([label, key]) => (
            <a
              key={label}
              href="#"
              role="tab"
              aria-selected={tab === key}
              onClick={(e) => {
                pre(e);
                setTab(key);
              }}
              style={sx(tabStyle(tab === key))}
            >
              <span>{label}</span>
            </a>
          ))}
      </div>

      {!setup && <Station role={FA ? 'admin' : dev ? 'dev' : 'operator'} canWrite={canWrite} readOnlyReason={writeTitle ?? null} onOpenIncident={openIncident} />}

      {setup && (
        <>
          <div data-screen-label="Verdict" style={sx('box-sizing:border-box;flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);padding:0;overflow:hidden;')} data-testid="pulse-verdict">
            <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:4px;padding:9px 14px 8px;border-left:3px solid transparent;')}>
              <span style={sx('width:8px;height:8px;border-radius:50%;flex:0 0 8px;align-self:center;background:' + SPEC[roll][2] + ';')} />
              <span style={sx('font-size:16px;font-weight:700;letter-spacing:-.02em;line-height:1.25;text-wrap:pretty;min-width:0;')}>
                <span>{verdict}</span>
              </span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>
                <span>{beat}</span>
              </span>
              <a
                href="#"
                onClick={(e) => {
                  pre(e);
                  setTab('Projected');
                }}
                style={sx('display:inline-flex;align-items:center;gap:8px;flex:1 1 240px;min-width:0;text-decoration:none;color:' + (soonest !== null && soonest <= 540 ? '#B3261E' : '#3C454F') + ';')}
              >
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;white-space:nowrap;')}>Projected</span>
                <span style={sx('flex:1 1 140px;min-width:0;font-size:16px;font-weight:700;line-height:1.4;text-wrap:pretty;')}>
                  <span>{foreLine}</span>
                </span>
                <span style={sx('font-size:14px;font-weight:700;color:#2F5C97;white-space:nowrap;')}>Open {'›'}</span>
              </a>
            </div>
          </div>

          <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:flex;flex-direction:column;gap:11px;padding-right:3px;')} aria-busy={health.loading || incs.loading ? 'true' : undefined}>
            {tab === 'Projected' && <ProjectedTab rows={proj.data?.projections ?? []} missing={!!proj.error} openIncident={openIncident} />}
            {tab === 'Models' && <ModelsTab />}
            {tab === 'Connectors' && <ConnectorsTab canWrite={canWrite} writeTitle={writeTitle} />}
            {tab === 'Incidents' && (
              <IncidentsTab
                incidents={incidents}
                v3={v3.data?.incidents ?? []}
                filter={incFilter}
                setFilter={setIncFilter}
                openId={incOpen}
                setOpenId={setIncOpen}
                ackedHere={ackedHere}
                onAcked={onAcked}
                canWrite={canWrite}
                dev={dev}
                devCanWrite={dev && roReason === null}
                writeTitle={writeTitle}
                now={now}
              />
            )}
            {tab === 'On-Call' && <OnCallTab incidents={incidents} canWrite={canWrite} writeTitle={writeTitle} />}
            {tab === 'Security' && <SecurityTab />}
          </div>
        </>
      )}
    </div>
  );
}
